@file:OptIn(ExperimentalMaterial3Api::class)

package us.laantech.laanmanage

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Menu
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.lifecycle.ViewModel
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.lifecycle.viewModelScope
import androidx.lifecycle.viewmodel.compose.viewModel
import androidx.lifecycle.viewmodel.initializer
import androidx.lifecycle.viewmodel.viewModelFactory
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import us.laantech.common.api.KtorClient
import us.laantech.laanmanage.api.clearAuthorizationCodeFromUrl
import us.laantech.laanmanage.api.getAuthorizationCodeFromUrl
import us.laantech.laanmanage.feature.employee.EmployeeManagementView
import us.laantech.laanmanage.feature.login.LoginView
import us.laantech.laanmanage.feature.store.department.StoreDepartmentsView
import us.laantech.laanmanage.ui.theme.BackofficeTheme
import us.laantech.laanmanage.ui.MAIN_MENU


data class NavRoute(val id: String, val title: String, val section: String, val content: @Composable () -> Unit)
data class NavSection(val title: String, val buttons: List<NavButton>)
data class NavButton(val label: String, val content: @Composable () -> Unit)


data class AppStateUiState(
    val isLoggedIn: Boolean = false,
    val isExchangingCode: Boolean = false,
    val pageTitle: String = "",
    val content: @Composable () -> Unit = { EmployeeManagementView() },
)

@Composable
@Preview
fun App(
    viewModel: AppViewModel = viewModel(
        factory = viewModelFactory {
            initializer { AppViewModel() }
        }
    )
) {

    val uiState by viewModel.uiState.collectAsStateWithLifecycle()
//
//    var screenSize by remember { mutableStateOf(IntSize.Zero) }
//    val isMobileLayout = screenSize == IntSize.Zero || screenSize.height > screenSize.width

    BackofficeTheme {
        if (uiState.isExchangingCode) {
            Box(
                modifier = Modifier.fillMaxSize(),
                contentAlignment = Alignment.Center
            ) {
                Column(horizontalAlignment = Alignment.CenterHorizontally) {
                    CircularProgressIndicator()
                    Spacer(Modifier.height(16.dp))
                    Text("Signing in...")
                }
            }
        } else if (uiState.isLoggedIn) {
            BoxWithConstraints {
                val isMobileLayout = maxWidth < maxHeight

                val drawerState = rememberDrawerState(DrawerValue.Closed)
                val scope = rememberCoroutineScope()

                AdaptiveNavigationDrawer(
                    isMobileLayout = isMobileLayout,
                    drawerState = drawerState,
                    drawerContent = {
                        AdaptiveDrawerSheet(
                            isMobileLayout = isMobileLayout,
                            modifier = Modifier.widthIn(max = 200.dp),
                            // 0, 40, 104
                            drawerContainerColor = Color(0, 40, 104),
                        ) {
                            Column(Modifier.verticalScroll(rememberScrollState())) {
                                Text(
                                    "LAAN Backoffice",
                                    modifier = Modifier.padding(16.dp),
                                    style = MaterialTheme.typography.titleLarge,
                                    color = Color.White
                                )

                                MAIN_MENU.forEach { section ->
                                    HorizontalDivider()
                                    Text(
                                        section.title,
                                        modifier = Modifier.padding(16.dp),
                                        style = MaterialTheme.typography.titleMedium,
                                        fontWeight = FontWeight.Bold,
                                        color = Color.Gray
                                    )
                                    section.items.forEach { item ->
                                        NavigationDrawerItem(
                                            label = { Text(item.title) },
                                            selected = false,
                                            onClick = {
                                                viewModel.setContent(item.title, item.destination)
                                                scope.launch { drawerState.close() }
                                            },
                                            colors = NavigationDrawerItemDefaults.colors(
                                                unselectedTextColor = Color.White,
                                                selectedTextColor = Color.White,
                                                unselectedContainerColor = Color(0, 40, 104),
                                                selectedContainerColor = Color(0, 60, 140),
                                            ),
                                            modifier = if (!isMobileLayout)
                                                Modifier.heightIn(max = 28.dp)
                                            else Modifier,

                                        )
                                    }
                                }

                                HorizontalDivider(modifier = Modifier.padding(top = 16.dp))
                                NavigationDrawerItem(
                                    label = { Text("Logout") },
                                    selected = false,
                                    onClick = { viewModel.logout() },
                                    colors = NavigationDrawerItemDefaults.colors(
                                        unselectedTextColor = Color.White,
                                        selectedTextColor = Color.White,
                                        unselectedContainerColor = Color(0, 40, 104),
                                        selectedContainerColor = Color(0, 60, 140),
                                    )
                                )
                            }
                        }
                    },
                ) {
                    // global Selection container causes recomposition errors in modals.
//                SelectionContainer {
//                }

                    Scaffold(
                        topBar = {
                            if (isMobileLayout) {
                                CenterAlignedTopAppBar(
                                    title = {
                                        Text(
                                            uiState.pageTitle.ifEmpty { "LAAN Backoffice" },
                                            style = MaterialTheme.typography.titleLarge,
                                            color = Color.White
                                        )
                                    },
                                    navigationIcon = {
                                        IconButton(onClick = {
                                            scope.launch {
                                                if (drawerState.isOpen) drawerState.close()
                                                else drawerState.open()
                                            }
                                        }) {
                                            Icon(Icons.Default.Menu, contentDescription = "Toggle menu", tint = Color.White)
                                        }
                                    },
                                    colors = TopAppBarDefaults.topAppBarColors(containerColor = Color(0, 40, 104))
                                )
                            }
                        }
                    ) { innerPadding ->

                        val topPadding = if (isMobileLayout) innerPadding.calculateTopPadding() else 16.dp
                        val bottomPadding = if (isMobileLayout) innerPadding.calculateBottomPadding() else 0.dp
                        val leftPadding = if (isMobileLayout) 4.dp else 16.dp
                        val rightPadding = if (isMobileLayout) 4.dp else 16.dp

                        Box(Modifier.padding(top = topPadding, start = leftPadding, end = rightPadding)) {
                            uiState.content()
                        }
                    }


                }
            }
        } else {
            LoginView()
        }
    }

}

@Composable
fun AdaptiveNavigationDrawer(
    isMobileLayout: Boolean,
    drawerState: DrawerState = rememberDrawerState(DrawerValue.Closed),
    drawerContent: @Composable () -> Unit,
    content: @Composable () -> Unit
) {
    if (isMobileLayout) {
        // Phones: slide-over drawer
        ModalNavigationDrawer(
            drawerState = drawerState,
            drawerContent = { drawerContent() },
            content = content,
        )
    } else {
        // Large tablets/desktops: permanent drawer
        PermanentNavigationDrawer(
            drawerContent = { drawerContent() },
            content = content,
        )
    }
}


@Composable
fun AdaptiveDrawerSheet(
    isMobileLayout: Boolean,
    modifier: Modifier = Modifier,
    drawerContainerColor: Color = DrawerDefaults.containerColor,
    content: @Composable () -> Unit
) {
    if (isMobileLayout) {
        // Phones: slide-over drawer
        ModalDrawerSheet(
            modifier = modifier,
            drawerContainerColor = drawerContainerColor,
        ) { content() }
    } else {
        // Large tablets/desktops: permanent drawer
        PermanentDrawerSheet(
            modifier = modifier,
            drawerContainerColor = drawerContainerColor,
        ) { content() }
    }
}




class AppViewModel : ViewModel() {

    private val _uiState = MutableStateFlow(AppStateUiState())
    val uiState = _uiState.asStateFlow()

    init {
        // AppViewModel is the first thing App() creates, so this runs before any other
        // ViewModel touches KtorClient.instance.
        KtorClient.configure(ENV)

        // authState is the single source of truth for login state; mirror it into the UI.
        // storeTokens()/clearTokens() (login, refresh, logout, auth failure) all flip it.
        viewModelScope.launch {
            KtorClient.instance.authState.collect { loggedIn ->
                _uiState.value = _uiState.value.copy(isLoggedIn = loggedIn)
            }
        }

        val code = getAuthorizationCodeFromUrl()
        if (code != null) {
            // Web redirect-return path: exchanging the code flips authState on success.
            _uiState.value = _uiState.value.copy(isExchangingCode = true)
            viewModelScope.launch {
                try {
                    KtorClient.instance.exchangeCodeForTokens(code)
                    clearAuthorizationCodeFromUrl()
                } catch (e: Exception) {
                    println("Failed to exchange authorization code: $e")
                    clearAuthorizationCodeFromUrl()
                } finally {
                    _uiState.value = _uiState.value.copy(isExchangingCode = false)
                }
            }
        } else {
            // Silent refresh if the access token expired but the refresh token is still
            // valid; hasValidToken() flips authState via storeTokens() when it succeeds.
            viewModelScope.launch {
                KtorClient.instance.hasValidToken()
            }
        }
    }

    fun setContent(title: String, content: @Composable () -> Unit) {
        _uiState.value = _uiState.value.copy(pageTitle = title, content = content)
    }

    fun logout() {
        // clearTokens() flips authState to false, which the collector mirrors into isLoggedIn.
        KtorClient.instance.clearTokens()
    }

}


